import { Component, HostListener, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DateUtil, Time } from 'huddyapi';
import moment, { DurationInputArg1, Moment } from 'moment';

type DayData = {
  dayOfMonth: number;
  epochDay: number;
};

type MonthData = {
  startEpochDay: number;
  dayCount: number;
  year: number;
  name: string;
  weeks: DayData[][];
};

@Component({
  selector: 'date-select-dialog',
  templateUrl: 'date-select-dialog.component.html',
  styleUrls: ["./date-select-dialog.component.scss"]
})
export class DateSelectDialog {
  today: number;
  monthData: MonthData;
  dayNames: string[];
  start: number;
  end: number;
  hovering: number;

  constructor(public dialogRef: MatDialogRef<DateSelectDialog>, public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DateSelectDialogData) {
    const time: Time = new Time();
    this.dayNames = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];
    this.hovering = null;
    this.start = (data.start != null) ? data.start : data.end;
    this.end = (data.end != null) ? data.end : data.start;
    this.today = time.getEpochDay();
    this.monthData = this.getMonthData((this.data.startingPosition != null) ? this.data.startingPosition : this.today);
  }

  getMonthData(epochDay: number, change: DurationInputArg1 = 0, units: moment.unitOfTime.DurationConstructor = 'months'): MonthData {
    let moment: Moment = DateUtil.fromEpochDay(epochDay).momentVal.clone();
    moment.add(change, units);
    moment.startOf('month');
    const start: number = DateUtil.toEpochDay(moment.year(), moment.month(), moment.date());
    let dayOfWeek: number = moment.day();
    moment.endOf('month');
    const end: number = DateUtil.toEpochDay(moment.year(), moment.month(), moment.date());
    const dayCount: number = end - start + 1;
    let result: MonthData = {
      startEpochDay: start,
      dayCount: dayCount,
      year: moment.year(),
      name: moment.format('MMMM'),
      weeks: []
    };
    let rowIndex: number = 0, shouldAddRow: boolean = true;
    for(let dayNum = 1; dayNum <= dayCount; dayNum++) {
      if(shouldAddRow) {
        shouldAddRow = false;
        result.weeks.push([]);
      }
      result.weeks[rowIndex].push({
        dayOfMonth: dayNum,
        epochDay: start + dayNum - 1
      });
      dayOfWeek++;
      if(dayOfWeek == 7) {
        dayOfWeek = 0;
        rowIndex++;
        shouldAddRow = true;
      }
    }
    return result;
  }

  changeDate(count: DurationInputArg1, units: moment.unitOfTime.DurationConstructor) {
    this.monthData = this.getMonthData(this.monthData.startEpochDay, count, units);
  }

  isToday(day: DayData): boolean {
    return day.epochDay == this.today;
  }

  isStart(day: DayData): boolean {
    return this.start != null && day.epochDay == this.start;
  }

  isEnd(day: DayData): boolean {
    return this.end != null && day.epochDay == this.end;
  }

  isInner(day: DayData): boolean {
    return this.start != null && this.end != null && day.epochDay > this.start && day.epochDay < this.end;
  }

  isHoverDay(day: DayData): boolean {
    return this.data.range && this.start != null && this.start == this.end && day.epochDay > this.start && day.epochDay == this.hovering;
  }

  isHoverRange(day: DayData): boolean {
    return this.data.range && this.start != null && this.start == this.end && day.epochDay >= this.start && day.epochDay < this.hovering;
  }

  isDisabled(day: DayData): boolean {
    return (this.data.earliestAllowed != null && day.epochDay < this.data.earliestAllowed)
        || (this.data.latestAllowed && day.epochDay > this.data.latestAllowed);
  }

  mouseEntered(day: DayData) {
    this.hovering = day.epochDay;
  }

  mouseLeft(day: DayData) {
    if(this.hovering == day.epochDay)
      this.hovering = null;
  }

  dayClicked(day: DayData) {
    if(this.isDisabled(day)
      || (this.data.earliestAllowed != null && day.epochDay < this.data.earliestAllowed)
      || (this.data.latestAllowed != null && day.epochDay > this.data.latestAllowed))
      return;
    if(this.start == null || this.start != this.end || day.epochDay < this.start || !this.data.range) {
      this.start = day.epochDay;
      this.end = day.epochDay;
    } else
      this.end = day.epochDay;
  }

  canConfirm(): boolean {
    return this.start != null && this.end != null;
  }

  cancel() {
    this.dialogRef.close(null);
  }

  confirm() {
    if(!this.canConfirm())
      return;
    this.dialogRef.close({
      start: this.start,
      end: this.end
    });
  }

  getDialogContainerStyles(): string {
    return (this.data && this.data.minWidth) ? ('min-width:' + this.data.minWidth + ';') : '';
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 'Enter':
        event.preventDefault();
        this.confirm();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.changeDate(-1, event.shiftKey ? 'years' : 'months');
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.changeDate(1, event.shiftKey ? 'years' : 'months');
        break;
      default:
    }
  }
}

export interface DateSelectDialogData {
  confirmText?: string;
  cancelText?: string;
  minWidth?: string;
  earliestAllowed?: number;
  latestAllowed?: number;
  start?: number;
  end?: number;
  range?: boolean;
  startingPosition?: number;
}

export interface DateSelectDialogResult {
  start: number;
  end: number;
}
